import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash, Drop, Lightning, ArrowsClockwise } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

export default function Conversioni() {
  const [rules, setRules] = useState([]);
  const [products, setProducts] = useState([]);
  const [creating, setCreating] = useState(false);
  const [executing, setExecuting] = useState(null); // rule being executed
  const [execQty, setExecQty] = useState("");
  const [execNotes, setExecNotes] = useState("");
  const [form, setForm] = useState(emptyForm());

  function emptyForm() {
    return {
      name: "",
      source_product_id: "",
      target_product_id: "",
      dilution_factor: 1.3,
      water_product_id: "",
      notes: "",
    };
  }

  const load = useCallback(async () => {
    const [r, p] = await Promise.all([api.get("/conversions/rules"), api.get("/products")]);
    setRules(r.data);
    setProducts(p.data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sourceCandidates = products.filter((p) =>
    ["semilavorato", "prodotto_finito"].includes(p.type)
  );
  const targetCandidates = products.filter((p) =>
    ["semilavorato", "prodotto_finito"].includes(p.type)
  );
  const waterCandidates = products.filter((p) => p.type === "materia_prima");

  const submitRule = async () => {
    if (!form.name.trim()) {
      toast.error("Inserisci un nome per la regola");
      return;
    }
    if (!form.source_product_id || !form.target_product_id) {
      toast.error("Seleziona prodotto sorgente e destinazione");
      return;
    }
    if (form.source_product_id === form.target_product_id) {
      toast.error("Sorgente e destinazione devono essere diversi");
      return;
    }
    if (!form.dilution_factor || Number(form.dilution_factor) <= 0) {
      toast.error("Fattore di diluizione non valido");
      return;
    }
    try {
      const payload = {
        name: form.name.trim(),
        source_product_id: form.source_product_id,
        target_product_id: form.target_product_id,
        dilution_factor: Number(form.dilution_factor),
        water_product_id: form.water_product_id || null,
        notes: form.notes || null,
      };
      await api.post("/conversions/rules", payload);
      toast.success("Regola creata");
      setCreating(false);
      setForm(emptyForm());
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteRule = async (id) => {
    if (!window.confirm("Eliminare questa regola di conversione?")) return;
    try {
      await api.delete(`/conversions/rules/${id}`);
      toast.success("Regola eliminata");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const submitExecute = async () => {
    const qty = Number(execQty);
    if (!qty || qty <= 0) {
      toast.error("Quantità non valida");
      return;
    }
    try {
      const { data } = await api.post("/conversions/execute", {
        rule_id: executing.id,
        source_quantity: qty,
        notes: execNotes || null,
      });
      toast.success(
        `Conversione eseguita: ${data.target_produced.toFixed(2)} ${
          executing.target_product_name
        } prodotti`
      );
      setExecuting(null);
      setExecQty("");
      setExecNotes("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const productById = (id) => products.find((p) => p.id === id);

  const previewQty = executing && execQty ? Number(execQty) * executing.dilution_factor : 0;
  const previewWater = executing && execQty
    ? Number(execQty) * (executing.dilution_factor - 1)
    : 0;
  const sourceStock = executing ? productById(executing.source_product_id)?.stock ?? 0 : 0;
  const waterStock = executing?.water_product_id
    ? productById(executing.water_product_id)?.stock ?? 0
    : null;

  return (
    <div className="p-8 lg:p-12 fade-up" data-testid="conversioni-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-2">
            Trasformazioni · Diluizione di Emergenza
          </div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight text-[#0A0A0A]">
            Conversioni
          </h1>
          <div className="text-sm text-[#4B5563] mt-2 max-w-2xl">
            Trasforma un semilavorato in un altro applicando un fattore di diluizione
            (es. CD VLR → CD VR ×1.3 con aggiunta di acqua).
          </div>
        </div>
        <button
          data-testid="create-conversion-btn"
          onClick={() => setCreating(true)}
          className="bg-[#1D3557] text-white px-5 h-12 inline-flex items-center gap-2 hover:bg-[#0A0A0A] transition-colors uppercase tracking-wider text-xs font-medium"
        >
          <Plus size={16} weight="bold" />
          Nuova Regola
        </button>
      </div>

      {rules.length === 0 ? (
        <div
          className="bg-white border border-dashed border-[#E5E7EB] p-16 text-center"
          data-testid="empty-conversions"
        >
          <ArrowsClockwise size={40} weight="duotone" color="#1D3557" className="mx-auto mb-4" />
          <div className="font-display text-lg text-[#0A0A0A] mb-2">
            Nessuna regola di conversione
          </div>
          <div className="text-sm text-[#4B5563]">
            Crea la prima regola per gestire le diluizioni di emergenza tra semilavorati.
          </div>
        </div>
      ) : (
        <div className="grid gap-4">
          {rules.map((r) => (
            <div
              key={r.id}
              className="bg-white border border-[#E5E7EB] p-6"
              data-testid={`conversion-rule-${r.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="font-display font-semibold text-lg text-[#0A0A0A] mb-3">
                    {r.name}
                  </div>
                  <div className="flex items-center gap-3 flex-wrap text-sm">
                    <div className="bg-[#F9FAFB] border border-[#E5E7EB] px-3 py-2">
                      <div className="text-[10px] uppercase tracking-wider text-[#4B5563]">
                        Sorgente
                      </div>
                      <div className="font-medium text-[#0A0A0A]">{r.source_product_name}</div>
                    </div>
                    <div className="font-mono-data text-[#1D3557] font-bold text-lg">
                      ×{r.dilution_factor}
                    </div>
                    {r.water_product_name && (
                      <>
                        <Drop size={18} weight="duotone" color="#457B9D" />
                        <div className="text-xs text-[#4B5563]">+ {r.water_product_name}</div>
                      </>
                    )}
                    <div className="text-xl text-[#4B5563]">→</div>
                    <div className="bg-[#E6F4F1] border border-[#2A9D8F]/30 px-3 py-2">
                      <div className="text-[10px] uppercase tracking-wider text-[#2A9D8F]">
                        Destinazione
                      </div>
                      <div className="font-medium text-[#0A0A0A]">{r.target_product_name}</div>
                    </div>
                  </div>
                  {r.notes && (
                    <div className="text-xs text-[#4B5563] mt-3 italic">{r.notes}</div>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    data-testid={`execute-conversion-${r.id}`}
                    onClick={() => setExecuting(r)}
                    className="bg-[#E63946] text-white px-4 h-10 inline-flex items-center gap-2 hover:bg-[#c92a37] transition-colors uppercase tracking-wider text-xs font-semibold"
                  >
                    <Lightning size={14} weight="fill" />
                    Esegui
                  </button>
                  <button
                    data-testid={`delete-conversion-${r.id}`}
                    onClick={() => deleteRule(r.id)}
                    className="border border-[#E5E7EB] text-[#E63946] px-4 h-10 inline-flex items-center gap-2 hover:bg-[#FBEDEE] transition-colors uppercase tracking-wider text-xs font-medium"
                  >
                    <Trash size={14} weight="bold" />
                    Elimina
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Rule Dialog */}
      <Dialog open={creating} onOpenChange={(v) => !v && setCreating(false)}>
        <DialogContent className="max-w-xl" data-testid="create-conversion-dialog">
          <DialogHeader>
            <DialogTitle>Nuova Regola di Conversione</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-2">
                Nome regola
              </label>
              <input
                data-testid="conv-name-input"
                className="w-full border border-[#E5E7EB] bg-white px-4 h-11 text-sm outline-none focus:ring-2 focus:ring-[#1D3557]"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Es. CD VLR → CD VR (×1.3 acqua)"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-2">
                  Prodotto sorgente
                </label>
                <Select
                  value={form.source_product_id}
                  onValueChange={(v) => setForm({ ...form, source_product_id: v })}
                >
                  <SelectTrigger data-testid="conv-source-select" className="rounded-none h-11">
                    <SelectValue placeholder="Seleziona…" />
                  </SelectTrigger>
                  <SelectContent>
                    {sourceCandidates.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-2">
                  Prodotto destinazione
                </label>
                <Select
                  value={form.target_product_id}
                  onValueChange={(v) => setForm({ ...form, target_product_id: v })}
                >
                  <SelectTrigger data-testid="conv-target-select" className="rounded-none h-11">
                    <SelectValue placeholder="Seleziona…" />
                  </SelectTrigger>
                  <SelectContent>
                    {targetCandidates
                      .filter((p) => p.id !== form.source_product_id)
                      .map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-2">
                  Fattore di diluizione
                </label>
                <input
                  type="number"
                  step="0.01"
                  data-testid="conv-factor-input"
                  className="w-full border border-[#E5E7EB] bg-white px-4 h-11 text-sm outline-none focus:ring-2 focus:ring-[#1D3557] font-mono-data"
                  value={form.dilution_factor}
                  onChange={(e) =>
                    setForm({ ...form, dilution_factor: e.target.value })
                  }
                />
                <div className="text-[10px] text-[#4B5563] mt-1">
                  Es. 1.3 → 100L sorgente diventano 130L destinazione
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-2">
                  Acqua / Diluente (opz.)
                </label>
                <Select
                  value={form.water_product_id || "__none__"}
                  onValueChange={(v) =>
                    setForm({ ...form, water_product_id: v === "__none__" ? "" : v })
                  }
                >
                  <SelectTrigger data-testid="conv-water-select" className="rounded-none h-11">
                    <SelectValue placeholder="Nessuno" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Nessuno —</SelectItem>
                    {waterCandidates.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-2">
                Note (opz.)
              </label>
              <textarea
                data-testid="conv-notes-input"
                className="w-full border border-[#E5E7EB] bg-white px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-[#1D3557]"
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setCreating(false)}
              className="border border-[#E5E7EB] px-5 h-10 text-xs uppercase tracking-wider text-[#4B5563]"
            >
              Annulla
            </button>
            <button
              onClick={submitRule}
              data-testid="conv-submit-btn"
              className="bg-[#1D3557] text-white px-5 h-10 text-xs uppercase tracking-wider"
            >
              Crea Regola
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Execute Dialog */}
      <Dialog open={!!executing} onOpenChange={(v) => !v && setExecuting(null)}>
        <DialogContent className="max-w-lg" data-testid="execute-conversion-dialog">
          <DialogHeader>
            <DialogTitle>Esegui Conversione</DialogTitle>
          </DialogHeader>
          {executing && (
            <div className="space-y-4">
              <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-4">
                <div className="font-display font-semibold text-base text-[#0A0A0A] mb-2">
                  {executing.name}
                </div>
                <div className="text-xs text-[#4B5563]">
                  {executing.source_product_name} ×{executing.dilution_factor}
                  {executing.water_product_name && ` (+ ${executing.water_product_name})`} →{" "}
                  {executing.target_product_name}
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-2">
                  Quantità di {executing.source_product_name} da convertire
                </label>
                <input
                  type="number"
                  step="0.01"
                  autoFocus
                  data-testid="exec-qty-input"
                  className="w-full border border-[#E5E7EB] bg-white px-4 h-12 text-lg font-mono-data outline-none focus:ring-2 focus:ring-[#E63946]"
                  value={execQty}
                  onChange={(e) => setExecQty(e.target.value)}
                  placeholder="Es. 500"
                />
                <div className="text-[10px] text-[#4B5563] mt-1">
                  Disponibili in stock: {sourceStock}{" "}
                  {productById(executing.source_product_id)?.unit || "L"}
                </div>
              </div>

              {execQty && Number(execQty) > 0 && (
                <div className="bg-[#FFF8E7] border border-[#F4A261]/40 p-4">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] mb-2">
                    Anteprima
                  </div>
                  <div className="space-y-1 text-sm font-mono-data">
                    <div className="flex justify-between">
                      <span>− {executing.source_product_name}</span>
                      <span className="text-[#E63946] font-semibold">
                        {Number(execQty).toFixed(2)}
                      </span>
                    </div>
                    {executing.water_product_name && (
                      <div className="flex justify-between">
                        <span>− {executing.water_product_name}</span>
                        <span className="text-[#E63946] font-semibold">
                          {previewWater.toFixed(2)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between border-t border-[#F4A261]/40 pt-1 mt-2">
                      <span>+ {executing.target_product_name}</span>
                      <span className="text-[#2A9D8F] font-bold text-base">
                        {previewQty.toFixed(2)}
                      </span>
                    </div>
                  </div>
                  {Number(execQty) > sourceStock && (
                    <div className="text-xs text-[#E63946] font-semibold mt-2">
                      ⚠ Stock sorgente insufficiente
                    </div>
                  )}
                  {waterStock !== null && previewWater > waterStock && (
                    <div className="text-xs text-[#E63946] font-semibold mt-2">
                      ⚠ Stock acqua insufficiente ({waterStock} disponibili)
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-2">
                  Note (opz.)
                </label>
                <input
                  data-testid="exec-notes-input"
                  className="w-full border border-[#E5E7EB] bg-white px-4 h-11 text-sm outline-none focus:ring-2 focus:ring-[#1D3557]"
                  value={execNotes}
                  onChange={(e) => setExecNotes(e.target.value)}
                  placeholder="Es. residuo batch BATCH-20260201-001"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <button
              onClick={() => setExecuting(null)}
              className="border border-[#E5E7EB] px-5 h-10 text-xs uppercase tracking-wider text-[#4B5563]"
            >
              Annulla
            </button>
            <button
              onClick={submitExecute}
              data-testid="exec-submit-btn"
              className="bg-[#E63946] text-white px-5 h-10 text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2"
            >
              <Lightning size={14} weight="fill" />
              Conferma Conversione
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
